import React from "react";
import { motion } from "framer-motion";
import { useI18n } from "@/i18n/I18nContext";

// Only reveal 3 of the 7 worksheets — the rest live behind the paywall.
export default function WorkingPages() {
  const { t } = useI18n();
  const visible = t.templates.items.slice(0, 3);
  const totalHidden = t.templates.items.length - 3;

  return (
    <section
      id="templates"
      className="relative py-24 md:py-32"
      style={{ background: "var(--cream-2)" }}
      data-testid="templates-section"
    >
      <div className="container-x">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-12 lg:gap-20 items-center">
          <div>
            <div className="chip-num mb-4">{t.templates.chip}</div>
            <h2 className="font-display font-medium text-[2.4rem] sm:text-[3rem] lg:text-[3.6rem] leading-[1] tracking-[-0.025em] text-[var(--ink)] mb-6">
              {t.templates.h2_a}{" "}
              <span className="font-editorial-italic text-[var(--marigold)]">
                {t.templates.h2_italic}
              </span>{" "}
              {t.templates.h2_b}
            </h2>
            <p className="text-[var(--muted)] text-base md:text-lg leading-relaxed max-w-[46ch]">
              {t.templates.p}
            </p>
            <div className="mt-8 flex items-center gap-3 text-sm text-[var(--muted)]">
              <div className="w-10 h-10 rounded-full bg-[var(--ink)] text-[var(--cream)] flex items-center justify-center">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M12 3v13m0 0l4-4m-4 4l-4-4M5 21h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <span>{t.templates.note}</span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {visible.map((w, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.55,
                  delay: i * 0.08,
                  ease: [0.2, 0.8, 0.2, 1],
                }}
                className={`relative bg-[var(--cream)] rounded-2xl p-5 md:p-6 border border-[rgba(15,31,23,0.08)] group hover:border-[var(--marigold)] transition-colors ${
                  i === 0 ? "sm:col-span-2" : ""
                }`}
              >
                <div className="flex items-start justify-between">
                  <span className="font-mono-caption text-[10px] tracking-[0.2em] text-[var(--marigold)] uppercase">
                    {t.templates.sheet_prefix} {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-[var(--muted)] opacity-40 group-hover:opacity-100 transition-opacity">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M7 17L17 7M17 7H8M17 7v9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                    </svg>
                  </span>
                </div>
                <h4 className="font-display text-xl md:text-2xl leading-tight mt-3 text-[var(--ink)]">
                  {w.name}
                </h4>
                <p className="mt-1 text-sm text-[var(--muted)]">{w.note}</p>
              </motion.div>
            ))}

            {/* Locked tile */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, delay: 0.24, ease: [0.2, 0.8, 0.2, 1] }}
              className="relative rounded-2xl p-5 md:p-6 sm:col-span-2 border border-dashed border-[rgba(15,31,23,0.25)] bg-[var(--cream)] overflow-hidden"
              data-testid="templates-teaser"
            >
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-full bg-[var(--ink)] text-[var(--cream)] flex items-center justify-center shrink-0">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <rect x="4" y="11" width="16" height="10" rx="2" stroke="currentColor" strokeWidth="1.6"/>
                    <path d="M8 11V8a4 4 0 0 1 8 0v3" stroke="currentColor" strokeWidth="1.6"/>
                  </svg>
                </div>
                <div className="flex-1">
                  <div className="font-mono-caption text-[10px] tracking-[0.2em] text-[var(--marigold)] uppercase mb-1">
                    {t.templates.teaser_chip} · +{totalHidden}
                  </div>
                  <div className="font-display text-lg md:text-xl text-[var(--ink)] leading-tight">
                    {t.templates.teaser_title}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
